/**
 * 관리 화면(메뉴 관리)이 그리는 트리 — 코드 카탈로그(매니페스트) + DB 오버라이드.
 *
 * ★ 왜 공용인가: 예전엔 "메뉴 구조 편집"만 오버라이드를 반영하고, "메뉴별 설정"과
 *   "권한별 미리보기"는 매니페스트 원본을 그렸다. 그래서 상위 메뉴를 옮겨 저장해도
 *   실제 사이드바만 바뀌고 관리 화면 두 곳은 그대로였다(사용자 신고). 트리를 만드는
 *   곳을 하나로 합쳐 그 어긋남을 구조적으로 없앤다. (visibleFor·resolveMenuLabel 과 같은 원칙)
 *
 * ★ "무엇이 메뉴인가"의 정본은 사이드바 코드다 — menuCodeDefaults(생성물)가 그 목록이다.
 *   매니페스트는 defaultRole·라벨을 보태는 보조 자료일 뿐이다. 예전엔 매니페스트를 돌면서
 *   트리를 만들어서, (a) 코드에서 내려간 옛 메뉴가 관리 화면에 유령으로 남고
 *   (b) 매니페스트에 등록을 깜빡한 실제 메뉴 13개가 아예 관리되지 않았다.
 *   이제 코드에 있는 것만, 코드에 있는 것은 전부 나온다 — 등록을 깜빡할 수 없다.
 */
import { MENU_ROLE_MANIFEST } from "./menuRoleManifest";
import { resolveMenuLabel } from "./menuLabels";
import { MENU_CODE_DEFAULTS, MENU_CODE_ORDER } from "./menuCodeDefaults";
import { menuSortOrder } from "./menuOverrides";
import type { MenuOverride } from "./menuOverrides";
import type { Role } from "../components/auth/AuthContext";
import { SETTINGS_MENUS, SETTINGS_ROOT_KEY, settingsMenuDef } from "./settingsMenus";
import { canCrossArea, homeAreaOf, makeMenuPlacement } from "./menuPlacement";

export type MenuArea = "crm" | "automation" | "settings";

export type AdminMenuNode = {
  menuKey: string;
  parentKey: string | null;
  /** 화면에 보여 줄 이름 (DB 오버라이드 > 사이드바 라벨 > 매니페스트 라벨) */
  label: string;
  /** DB 오버라이드 원본 — 입력창 값으로 쓴다(비어 있으면 "코드 기본값 사용") */
  labelOverride: string;
  pathOverride: string;
  iconOverride: string;
  /** 페이지 헤더 제목 오버라이드 — 비어 있으면 메뉴 이름이 제목이다(DESIGN-AI §19) */
  pageTitleOverride: string;
  /** 코드 기본값 — 오버라이드가 없을 때 화면이 보여 줄 "지금 값" */
  codeLabel: string;
  codeIcon: string;
  codePath: string;
  /** 화면 입력칸에 채울 실제 값 (오버라이드 > 코드 기본값) */
  effectiveIcon: string;
  effectivePath: string;
  defaultRole: Role;
  isCustom: boolean;
  /** 이름·링크를 고칠 수 있는가. 루트(상단 네비)만 false. */
  editable: boolean;
  /**
   * 끌어서 옮길 수 있는가. 문서 템플릿은 전용 렌더러가 묶음 구조를 쥐고 있어 false —
   * 이름·링크는 바꿀 수 있고, 활성화·권한도 다른 메뉴와 똑같이 제어된다.
   */
  movable: boolean;
  /** 다른 대메뉴(CRM ↔ IT-CONSULTING)로 옮길 수 있는가. 문서 템플릿·설정·루트는 false. */
  crossArea: boolean;
  /** 코드가 정의된 대메뉴와 지금 보이는 대메뉴가 다르다 — 다른 대메뉴에서 옮겨 온 메뉴. */
  movedIn: boolean;
  /** 아이콘을 고칠 수 있는가. 지금은 루트만 false. */
  iconEditable: boolean;
  /** 등급 고정(설정의 계정 설정 등) */
  roleLocked?: boolean;
  /** 노출 고정(설정의 메뉴 권한 관리 등) */
  enabledLocked?: boolean;
  /** 화면 데이터 API 의 서버 등급 — 이보다 낮게 열면 관리 화면이 경고한다 */
  apiRole?: Role;
  depth: number;
  children: AdminMenuNode[];
};

export const AREA_TITLE: Record<MenuArea, string> = {
  crm: "CRM",
  automation: "IT-CONSULTING (문서 자동화)",
  settings: "설정",
};

/** 이 area 의 최상위 키 — 상단 네비가 각 영역의 루트다. */
export function areaRootKey(area: MenuArea): string {
  if (area === "settings") return SETTINGS_ROOT_KEY;
  return area === "crm" ? "topnav:CRM" : "topnav:IT-CONSULTING";
}

/**
 * 관리 화면용 트리. 루트(상단 네비) 1개 아래 섹션(1depth) → 항목(2depth).
 * @param overrides DB 오버라이드. 빈 배열이면 매니페스트 순서·이름 그대로.
 */
export function buildAdminTree(area: MenuArea, overrides: MenuOverride[]): AdminMenuNode {
  // 설정은 자기 영역 행만. CRM·IT 는 키로 찾는다 — 다른 대메뉴로 옮겨 간 행은 area 가 다르다(menuPlacement).
  const byKey = new Map(overrides.filter((o) => (area === "settings" ? o.area === "settings" : o.area !== "settings"))
    .map((o) => [o.menu_key, o]));
  const placement = makeMenuPlacement(overrides);
  const rootKey = areaRootKey(area);

  const make = (
    menuKey: string, fallbackLabel: string, defaultRole: Role, depth: number,
    parentKey: string | null, editable: boolean, movable = editable, iconEditable = editable
  ): AdminMenuNode => {
    const o = byKey.get(menuKey);
    const codeLabel = resolveMenuLabel(menuKey, fallbackLabel);
    const settingsDef = settingsMenuDef(menuKey);
    const code: { icon?: string; path?: string } = MENU_CODE_DEFAULTS[menuKey]
      || (settingsDef ? { icon: settingsDef.icon, path: settingsDef.path } : {});
    const iconOverride = (o as { icon?: string | null } | undefined)?.icon ?? "";
    return {
      menuKey,
      parentKey: o?.parent_key ?? parentKey,
      label: o?.label || codeLabel,
      labelOverride: o?.label ?? "",
      pathOverride: o?.path ?? "",
      iconOverride,
      pageTitleOverride: o?.page_title ?? "",
      codeLabel,
      codeIcon: code.icon ?? "",
      codePath: code.path ?? "",
      effectiveIcon: iconOverride || code.icon || "",
      effectivePath: o?.path || code.path || "",
      defaultRole,
      isCustom: Boolean(o?.is_custom),
      editable,
      movable,
      crossArea: area !== "settings" && movable && canCrossArea(menuKey),
      movedIn: area !== "settings" && homeAreaOf(menuKey) !== null && homeAreaOf(menuKey) !== area,
      iconEditable,
      depth,
      children: [],
    };
  };

  // 매니페스트는 defaultRole·라벨 보조용 — 존재 여부의 근거가 아니다.
  const manifestByKey = new Map(MENU_ROLE_MANIFEST.map((e) => [e.key, e]));
  const roleOf = (menuKey: string): Role => {
    const fromManifest = manifestByKey.get(menuKey)?.defaultRole;
    if (fromManifest) return fromManifest;
    const fromCode = MENU_CODE_DEFAULTS[menuKey]?.minRole;
    return (fromCode as Role) || "viewer";
  };
  // 라벨 폴백: 매니페스트 라벨 > 키 끝부분(코드에만 있는 새 메뉴)
  const fallbackLabelOf = (menuKey: string) =>
    manifestByKey.get(menuKey)?.label ?? menuKey.split(":").slice(-1)[0];

  // ★ 정렬값은 사이드바와 같은 함수(menuSortOrder)로 계산한다 — 각자 계산하면 어긋난다.
  const orderOf = (menuKey: string) => menuSortOrder(menuKey, byKey.get(menuKey)?.sort_order);

  // ── 코드 카탈로그 ─────────────────────────────────────────────────────────
  const sections: AdminMenuNode[] = [];
  const items: AdminMenuNode[] = [];
  const rootEntry = MENU_ROLE_MANIFEST.find((e) => e.key === rootKey);

  // ── 설정(/settings) — 카탈로그가 settingsMenus 에 있다(생성물 아님) ──────────
  if (area === "settings") {
    SETTINGS_MENUS.forEach((def) => {
      if (!def.parent) {
        const node = make(def.key, def.label, def.defaultRole, 1, rootKey, true);
        node.roleLocked = def.roleLocked;
        node.enabledLocked = def.alwaysVisible;
        sections.push(node);
      } else {
        // 계정 설정은 옮기지 않는다 — 다른 섹션 아래로 가면 그 섹션 등급을 물려받아 "누구나"가 깨진다.
        const node = make(def.key, def.label, def.defaultRole, 2, def.parent, true, !def.roleLocked, true);
        node.roleLocked = def.roleLocked;
        node.enabledLocked = def.alwaysVisible;
        node.apiRole = def.apiRole;
        items.push(node);
      }
    });
  }

  // ★ 목록도 순서도 사이드바 코드(생성물)를 따른다 — 코드에서 내려간 메뉴는 여기 없고,
  //   코드에 새로 생긴 메뉴는 매니페스트 등록 없이도 제자리에 나온다.
  //   대메뉴를 건너 옮긴 메뉴는 보이는 쪽(menuPlacement)에 나온다 — 사이드바와 같은 판정.
  if (area !== "settings") MENU_CODE_ORDER.forEach((menuKey) => {
    const parts = menuKey.split(":");
    if (parts.length === 2) {
      if (placement.sectionArea(menuKey) !== area) return;
      sections.push(make(menuKey, fallbackLabelOf(menuKey), roleOf(menuKey), 1, rootKey, true));
    } else if (parts.length === 3) {
      if (placement.itemArea(menuKey) !== area) return;
      // 문서 템플릿은 이름·링크·아이콘은 되고 이동만 안 된다(묶음 구조는 전용 렌더러 소관).
      const isDoc = !canCrossArea(menuKey) && parts[0] === "crm";
      items.push(make(menuKey, fallbackLabelOf(menuKey), roleOf(menuKey), 2, placement.itemParent(menuKey), true, !isDoc, true));
    }
  });

  // ── 문서 12종은 여기 없다 ──────────────────────────────────────────────────
  // 예전엔 문서별 권한 전용 행을 "DOCUMENTS" 아래에 보탰다. 그러자 사이드바에는 4줄인데
  // 메뉴 관리에는 13줄이 "사용" 체크된 채 떠서 헷갈렸고(사용자 지적), 그 행의 사용 체크는
  // 실제로 아무것도 막지 않았다 — 문서 사용 여부는 crm_document_catalog.enabled 가 갖는다.
  // 문서별 등급·사용 여부는 /settings/documents(문서 관리) 한 곳에서 다룬다.
  // 권한 키(crm:document-automation:<docKey>)는 그대로라 등급 상속(섹션 → 문서)도 그대로다.

  // ── 직접 추가한 메뉴 ──────────────────────────────────────────────────────
  const known = new Set([...sections, ...items].map((n) => n.menuKey));
  overrides
    .filter((o) => o.is_custom && !known.has(o.menu_key) && (area === "settings"
      ? o.area === "settings"
      // 커스텀 항목은 부모 섹션의 대메뉴를 따른다(사이드바와 같은 판정).
      : o.area !== "settings" && (o.parent_key ? placement.sectionArea(o.parent_key) === area : o.area === area)))
    .forEach((o) => {
      const node = make(o.menu_key, o.label || "(이름 없음)", "viewer", o.parent_key ? 2 : 1, o.parent_key, true);
      node.isCustom = true;
      (o.parent_key ? items : sections).push(node);
    });

  // ── 조립 ──────────────────────────────────────────────────────────────────
  sections.sort((a, b) => orderOf(a.menuKey) - orderOf(b.menuKey) || a.menuKey.localeCompare(b.menuKey));
  const sectionByKey = new Map(sections.map((s) => [s.menuKey, s]));
  items.forEach((item) => {
    // 없어진 섹션을 가리키면 코드 기본 섹션으로 되돌린다 — 항목이 사라지면 안 된다.
    const parent = (item.parentKey && sectionByKey.get(item.parentKey))
      || sectionByKey.get(item.menuKey.split(":").slice(0, 2).join(":"));
    if (parent) {
      item.parentKey = parent.menuKey;
      parent.children.push(item);
    } else {
      // 코드 섹션조차 못 찾으면 루트 직속으로 올린다(숨기지 않는다).
      item.parentKey = rootKey;
      item.depth = 1;
      sections.push(item);
    }
  });
  sections.forEach((s) => s.children.sort((a, b) => orderOf(a.menuKey) - orderOf(b.menuKey) || a.menuKey.localeCompare(b.menuKey)));

  const root = make(rootKey, rootEntry?.label ?? AREA_TITLE[area], rootEntry?.defaultRole ?? "viewer", 0, null, false, false, false);
  if (area === "settings") {
    // 설정 루트는 묶음 표시일 뿐 판정에 쓰이지 않는다(설정 섹션의 부모가 아니다) — 조작 칸을 잠근다.
    root.defaultRole = "guest";
    root.roleLocked = true;
    root.enabledLocked = true;
  }
  root.children = sections;
  return root;
}

/** 트리를 화면에 그릴 순서대로 평평하게 편다(루트 포함). */
export function flattenAdminTree(root: AdminMenuNode): AdminMenuNode[] {
  const out: AdminMenuNode[] = [];
  const walk = (node: AdminMenuNode) => { out.push(node); node.children.forEach(walk); };
  walk(root);
  return out;
}
